import { collection, query, getDocs, where } from 'firebase/firestore';
import { db } from '../config/firebase';

export interface EmployeeNameMapping {
  email: string;
  displayName: string;
}

export interface EmployeeNamesService {
  getEmployeeNames(): Promise<EmployeeNameMapping[]>;
  getNameFromEmail(email: string): Promise<string | null>;
  getEmailFromDisplayName(displayName: string): Promise<string | null>;
  clearCache(): void;
}

const CACHE_TTL = 5 * 60 * 1000; // 5 minutes
const CACHE_KEY = 'employeeNamesCache';

interface CacheData {
  mappings: EmployeeNameMapping[];
  timestamp: number;
  emailToName: Map<string, string>;
  nameToEmail: Map<string, string>;
}

// Load cache from localStorage
const loadCache = (): CacheData => {
  const cached = localStorage.getItem(CACHE_KEY);
  if (cached) {
    try {
      const data = JSON.parse(cached);
      // Reconstruct Maps from plain objects
      return {
        ...data,
        emailToName: new Map(Object.entries(data.emailToName || {})),
        nameToEmail: new Map(Object.entries(data.nameToEmail || {}))
      };
    } catch (e) {
      return createEmptyCache();
    }
  }
  return createEmptyCache();
};

const createEmptyCache = (): CacheData => ({
  mappings: [],
  timestamp: 0,
  emailToName: new Map(),
  nameToEmail: new Map()
});

// Save cache to localStorage
const saveCache = (cache: CacheData) => {
  try {
    // Convert Maps to plain objects for JSON serialization
    const serializable = {
      ...cache,
      emailToName: Object.fromEntries(cache.emailToName),
      nameToEmail: Object.fromEntries(cache.nameToEmail)
    };
    localStorage.setItem(CACHE_KEY, JSON.stringify(serializable));
  } catch (e) {
    // Silently fail - no need to log cache errors
  }
};

const emailsCollection = collection(db, 'allowedEmails');

export const employeeNamesService: EmployeeNamesService = {
  async getEmployeeNames(): Promise<EmployeeNameMapping[]> {
    try {
      // Check if cache is valid
      const cache = loadCache();
      const now = Date.now();
      if (cache.mappings.length > 0 && now - cache.timestamp < CACHE_TTL) {
        return cache.mappings;
      }
      
      // Get all documents from allowedEmails collection
      const snapshot = await getDocs(emailsCollection);
      
      // Map the documents to EmployeeNameMapping
      const mappings = snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          email: data.email.toLowerCase().trim(),
          displayName: data.displayName || data.email
        };
      });

      // Build lookup maps
      const emailToName = new Map(mappings.map(m => [m.email, m.displayName]));
      const nameToEmail = new Map(mappings.map(m => [m.displayName, m.email]));
      
      // Update cache with all data
      const newCache: CacheData = {
        mappings,
        timestamp: now,
        emailToName,
        nameToEmail
      };
      saveCache(newCache);
      
      return mappings;
    } catch (error) {
      // Return cache if available, even if expired
      const cache = loadCache();
      return cache.mappings;
    }
  },

  async getNameFromEmail(email: string): Promise<string | null> {
    if (!email) return email;

    const normalizedEmail = email.toLowerCase().trim();
    const cache = loadCache();
    
    // Check cache first
    if (cache.emailToName.has(normalizedEmail)) {
      return cache.emailToName.get(normalizedEmail)!;
    }

    try {
      // Query Firestore directly if not in cache
      const q = query(emailsCollection, where('email', '==', normalizedEmail));
      const querySnapshot = await getDocs(q);
      
      if (!querySnapshot.empty) {
        const data = querySnapshot.docs[0].data();
        const displayName = data.displayName || email;
        
        // Update cache with this single mapping
        cache.emailToName.set(normalizedEmail, displayName);
        cache.nameToEmail.set(displayName, normalizedEmail);
        saveCache(cache);
        
        return displayName;
      }
      
      return email;
    } catch (error) {
      return email;
    }
  },

  async getEmailFromDisplayName(displayName: string): Promise<string | null> {
    if (!displayName) return null;

    const cache = loadCache();
    
    // Check cache first
    if (cache.nameToEmail.has(displayName)) {
      return cache.nameToEmail.get(displayName)!;
    }

    try {
      // Query Firestore directly if not in cache
      const snapshot = await getDocs(emailsCollection);
      const match = snapshot.docs.find(doc => doc.data().displayName === displayName);
      
      if (match) {
        const email = match.data().email;
        
        // Update cache with this single mapping
        cache.emailToName.set(email, displayName);
        cache.nameToEmail.set(displayName, email);
        saveCache(cache);
        
        return email;
      }
      
      return null;
    } catch (error) {
      return null;
    }
  },

  clearCache() {
    localStorage.removeItem(CACHE_KEY);
  }
};
